import React, { useState } from "react";
import { Link } from "gatsby";
import { Button } from "@/components/ui/button";
import { NavbarProps, MenuProps, MenuItem } from "@/types/navbar";
import HamburgerButton from "./shared/HamburgerButtons";
import { getMenuItems } from "./shared/menuItems";
import { defaultNavbarContent } from "./defaultNavbarContent";
import { trackEvent } from "@/components/analytics";
import { FocusScope } from "@radix-ui/react-focus-scope";
import { useEscapeKey } from "./shared/useEscapeKey";

interface MobileMenuProps extends MenuProps {
  navbarSecondaryCTA?: string;
  navbarSecondaryCTAUrl?: string;
  onClose: () => void;
}

function MobileMenu({
  menuItems,
  navbarSecondaryCTA,
  navbarSecondaryCTAUrl,
  onClose,
}: MobileMenuProps) {
  useEscapeKey(onClose);

  return (
    <FocusScope trapped>
    <div className="fixed inset-0 top-[72px] z-40 bg-background/95 md:hidden">
      <div className="flex flex-col gap-6 p-6">
        {menuItems.map(item =>
          item.isDropdown ? (
            <div key={item.label} className="relative">
              <button className="w-full text-left text-lg font-semibold text-foreground/80 transition duration-200 hover:text-foreground">
                {item.label}
              </button>
              <div className="mt-4 flex flex-col gap-2 pl-4">
                {item.dropdownItems?.map(dropdownItem => (
                  <Link
                    key={dropdownItem.slug}
                    to={dropdownItem.href}
                    className="block rounded-md px-3 py-2 text-lg text-foreground/70 transition-colors hover:bg-accent hover:text-foreground active:bg-accent/80"
                  >
                    {dropdownItem.name}
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            <Link
              key={item.label}
              to={item.href}
              className="rounded-md px-3 py-2 text-lg text-foreground/70 transition-colors hover:bg-accent hover:text-foreground active:bg-accent/80"
            >
              {item.label}
            </Link>
          )
        )}
        {navbarSecondaryCTA && navbarSecondaryCTAUrl && (
          <Link
            to={navbarSecondaryCTAUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-md px-3 py-2 text-lg text-foreground/70 transition-colors hover:bg-accent hover:text-foreground active:bg-accent/80"
            onClick={() => trackEvent("navbar_secondary_cta_click", { label: navbarSecondaryCTA })}
          >
            {navbarSecondaryCTA}
          </Link>
        )}
      </div>
    </div>
    </FocusScope>
  );
}

/**
 * Prevents navbar text from shifting when font weight changes on hover by
 * having an invisible version of the semibold text reserve space
 */
function NavMenuItem({ children }: { children: React.ReactNode }) {
  return (
    <span className="relative">
      <span className="invisible font-semibold">{children}</span>
      <span className="absolute left-0 top-0 w-full transition-colors duration-200 hover:font-semibold hover:text-foreground group-hover:font-semibold group-hover:text-foreground">
        {children}
      </span>
    </span>
  );
}

function DesktopMenu({ menuItems }: MenuProps) {
  return (
    <div className="hidden items-center gap-6 md:flex">
      {menuItems.map(item =>
        item.isDropdown ? (
          <div key={item.label} className="group relative flex items-center">
            <button className="relative bg-transparent text-foreground/70 transition duration-200 hover:text-foreground">
              <NavMenuItem>{item.label}</NavMenuItem>
            </button>
            <div className="absolute left-0 top-full z-50 hidden pt-1 group-hover:block">
              <div className="w-48 rounded border bg-background py-1 shadow-lg">
                {item.dropdownItems?.map(dropdownItem => (
                  <Link
                    key={dropdownItem.slug}
                    to={dropdownItem.href}
                    className="block px-4 py-2 text-foreground/70 hover:bg-accent hover:text-foreground"
                  >
                    {dropdownItem.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <Link
            key={item.label}
            to={item.href}
            className="relative text-foreground/70 transition duration-200 hover:text-foreground"
          >
            <NavMenuItem>{item.label}</NavMenuItem>
          </Link>
        )
      )}
    </div>
  );
}

export default function Navbar({
  services,
  primaryCTA = defaultNavbarContent.primaryCTA,
  primaryCTAUrl = defaultNavbarContent.primaryCTAUrl,
  secondaryCTA = defaultNavbarContent.secondaryCTA,
  secondaryCTAUrl = defaultNavbarContent.secondaryCTAUrl,
  navbarSecondaryCTA,
  navbarSecondaryCTAUrl,
  logoUrl = defaultNavbarContent.logoUrl,
  enableResources = true,
  ...forwardedProps
}: NavbarProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const menuItems = getMenuItems(services, enableResources);

  return (
    <nav
      className="fixed top-0 z-50 flex h-16 w-full items-center justify-between border-b-2 bg-background px-6"
      {...forwardedProps}
    >
      <div className="flex h-12 w-12 items-center gap-2">
        <Link to="/" className="flex items-center" aria-label="Logo - go to homepage">
          <img
            src={logoUrl}
            alt="Logo"
            fetchPriority="high"
            className="h-12 w-auto max-w-12 object-contain text-primary"
          />
        </Link>
      </div>
      <div className="z-50 md:hidden">
        <HamburgerButton isOpen={isMobileMenuOpen} toggleMenu={toggleMobileMenu} />
      </div>
      {isMobileMenuOpen && (
        <MobileMenu
          menuItems={menuItems}
          navbarSecondaryCTA={navbarSecondaryCTA}
          navbarSecondaryCTAUrl={navbarSecondaryCTAUrl}
          onClose={() => setIsMobileMenuOpen(false)}
        />
      )}
      <DesktopMenu menuItems={menuItems} />
      <div className="flex gap-4">
        <Button asChild variant="outline" className="hidden font-semibold md:flex">
          <Link to={secondaryCTAUrl}>{secondaryCTA}</Link>
        </Button>
        {navbarSecondaryCTA && navbarSecondaryCTAUrl && (
          <Button
            asChild
            variant="outline"
            className="hidden font-semibold md:flex"
          >
            <Link to={navbarSecondaryCTAUrl} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("navbar_secondary_cta_click", { label: navbarSecondaryCTA })}>
              {navbarSecondaryCTA}
            </Link>
          </Button>
        )}
        <Button asChild variant="default" className="font-semibold">
          <Link to={primaryCTAUrl}>{primaryCTA}</Link>
        </Button>
      </div>
    </nav>
  );
}
